import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';

const CONSENT_KEY = 'fincalc_cookie_consent';
const CONSENT_VERSION = 'v2'; // bump this to re-show banner after policy changes

const CookieConsentBanner: React.FC = () => {
  const { isDark } = useTheme();
  const location = useLocation();
  const isCookiePolicyPage = 
    location.pathname.startsWith('/cookie-policy') ||
    location.pathname.startsWith('/privacy-policy') ||
    location.pathname.startsWith('/terms-of-service') ||
    location.pathname.startsWith('/editorial-policy') ||
    location.pathname.startsWith('/disclaimer');
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      const cookieMatches = document.cookie.includes('cookieconsent_status=allow') ||
                            document.cookie.includes('cookieconsent_status=deny') ||
                            document.cookie.includes('cookieconsent_status=dismiss') ||
                            document.cookie.includes('cookieconsent_status=essential') ||
                            document.cookie.includes('cookie_consent=');
      if (saved && saved.includes(CONSENT_VERSION)) {
        return false;
      }
      if (cookieMatches) {
        return false;
      }
      return true;
    } catch {
      return true;
    }
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const handleUpdate = () => {
      try {
        const saved = localStorage.getItem(CONSENT_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.version === CONSENT_VERSION) {
            setVisible(false);
          }
        }
      } catch {}
    };
    window.addEventListener('cookie-consent-updated', handleUpdate);
    return () => window.removeEventListener('cookie-consent-updated', handleUpdate);
  }, []);

  const handleAccept = () => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        state: 'accepted',
        timestamp: new Date().toISOString(),
        analytics: true,
        advertising: true,
      }));
    } catch {}

    try {
      document.cookie = "cookieconsent_status=allow; path=/; max-age=31536000; SameSite=Lax";
      document.cookie = "cookie_consent=true; path=/; max-age=31536000; SameSite=Lax";
    } catch {}

    setVisible(false);

    // Load GA4 now that consent is given (if not already loaded)
    if (typeof window !== 'undefined' && !window.gtag) {
      const script = document.createElement('script');
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-F81GEFE6V4';
      script.async = true;
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      const gtag = (...args: any[]) => window.dataLayer.push(args);
      window.gtag = gtag as any;
      gtag('js', new Date());
      gtag('config', 'G-F81GEFE6V4', { anonymize_ip: true });
    }

    // Load AdSense now that consent is given (if not already loaded)
    if (typeof window !== 'undefined' && !window.adsbygoogle) {
      window.adsbygoogle = window.adsbygoogle || [];
      const adScript = document.createElement('script');
      adScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3740676760592264';
      adScript.async = true;
      adScript.crossOrigin = 'anonymous';
      document.head.appendChild(adScript);
    }
  };

  const handleDecline = () => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        state: 'declined',
        timestamp: new Date().toISOString(),
        analytics: false,
        advertising: false,
      }));
    } catch {}

    try {
      document.cookie = "cookieconsent_status=deny; path=/; max-age=31536000; SameSite=Lax";
      document.cookie = "cookie_consent=false; path=/; max-age=31536000; SameSite=Lax";
    } catch {}

    setVisible(false);
  };

  const handleEssentialOnly = () => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        state: 'essential',
        timestamp: new Date().toISOString(),
        analytics: false,
        advertising: false,
      }));
    } catch {}

    try {
      document.cookie = "cookieconsent_status=dismiss; path=/; max-age=31536000; SameSite=Lax";
      document.cookie = "cookie_consent=essential; path=/; max-age=31536000; SameSite=Lax";
    } catch {}

    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop blur overlay */}
      {!isCookiePolicyPage && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-[9998]"
          onClick={() => {}} // prevent accidental dismiss
          aria-hidden="true"
        />
      )}

      {/* Cookie Banner */}
      <div
        role="dialog"
        aria-label="Cookie Consent"
        aria-modal="true"
        id="cookie-consent-banner"
        className={`fixed bottom-0 left-0 right-0 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[9999] 
          rounded-2xl overflow-hidden shadow-2xl transition-all duration-500
          ${isDark
            ? 'bg-slate-900/95 border border-white/10 shadow-black/60'
            : 'bg-white/98 border border-slate-200 shadow-slate-300/60'
          }`}
        style={{ backdropFilter: 'blur(20px)' }}
      >
        {/* Top accent line */}
        <div className="h-[3px] w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-indigo-600" />

        <div className="p-4 sm:p-5">
          {/* Header */}
          <div className="flex items-start gap-2 sm:gap-3 mb-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
              <span className="text-sm sm:text-lg">🍪</span>
            </div>
            <div className="min-w-0">
              <h3 className={`text-xs sm:text-sm font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                We value your privacy
              </h3>
              <p className={`text-[10px] sm:text-[11px] font-medium mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                FinCalc uses cookies for analytics and personalised ads
              </p>
            </div>
          </div>

          {/* Body */}
          <p className={`text-xs leading-relaxed mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            We use cookies to improve your experience, show relevant ads via Google AdSense,
            and analyse site traffic with Google Analytics 4. Third-party vendors, including
            Google, use the DART cookie to serve ads based on your visits to this and other sites.
          </p>

          {/* Expandable details */}
          {showDetails && (
            <div className="mb-4 text-xs space-y-2.5">
              {/* Essential Cookies */}
              <div className={`p-2.5 rounded-xl border ${
                isDark ? 'bg-emerald-950/20 border-emerald-500/20 text-slate-300' : 'bg-emerald-50/50 border-emerald-100 text-slate-700'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                    Essential Cookies
                  </span>
                  <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-500 dark:text-indigo-400 hover:underline flex items-center gap-0.5 font-bold">
                    Read More <span className="material-symbols-outlined text-[10px]">open_in_new</span>
                  </a>
                </div>
                <p className="text-[11px] leading-snug opacity-95">Always active. Required for the site to function and session authentication.</p>
              </div>

              {/* Analytics Cookies */}
              <div className={`p-2.5 rounded-xl border ${
                isDark ? 'bg-indigo-950/20 border-indigo-500/20 text-slate-300' : 'bg-indigo-50/50 border-indigo-100 text-slate-700'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">bar_chart</span>
                    Analytics Cookies
                  </span>
                  <a href="https://policies.google.com/privacy?hl=en-GB" target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-500 dark:text-indigo-400 hover:underline flex items-center gap-0.5 font-bold">
                    Read More <span className="material-symbols-outlined text-[10px]">open_in_new</span>
                  </a>
                </div>
                <p className="text-[11px] leading-snug opacity-95">Google Analytics 4 (GA4 ID: G-F81GEFE6V4). Anonymous usage data to help us improve the platform.</p>
              </div>

              {/* Advertising Cookies */}
              <div className={`p-2.5 rounded-xl border ${
                isDark ? 'bg-violet-950/20 border-violet-500/20 text-slate-300' : 'bg-violet-50/50 border-violet-100 text-slate-700'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-violet-600 dark:text-violet-400 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">ads_click</span>
                    Advertising Cookies
                  </span>
                  <a href="https://support.google.com/adsense/answer/48182?sjid=14695444302049797352-NC" target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-500 dark:text-indigo-400 hover:underline flex items-center gap-0.5 font-bold">
                    Read More <span className="material-symbols-outlined text-[10px]">open_in_new</span>
                  </a>
                </div>
                <p className="text-[11px] leading-snug opacity-95">Google AdSense / DART cookie for personalised ads.</p>
                <div className="text-[10px] mt-1.5 pt-1.5 border-t border-slate-200/50 dark:border-slate-800/50 flex flex-wrap gap-x-2 gap-y-1 opacity-90 font-medium">
                  <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline flex items-center gap-0.5">
                    Opt out of Google ads <span className="material-symbols-outlined text-[9px]">open_in_new</span>
                  </a>
                  <span>·</span>
                  <a href="https://www.networkadvertising.org/managing/opt_out.asp" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline flex items-center gap-0.5">
                    NAI opt-out <span className="material-symbols-outlined text-[9px]">open_in_new</span>
                  </a>
                </div>
              </div>

              {/* Payment Cookies */}
              <div className={`p-2.5 rounded-xl border ${
                isDark ? 'bg-teal-950/20 border-teal-500/20 text-slate-300' : 'bg-teal-50/50 border-teal-100 text-slate-700'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-teal-600 dark:text-teal-400 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">credit_card</span>
                    Payment Cookies
                  </span>
                  <a href="https://razorpay.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-500 dark:text-indigo-400 hover:underline flex items-center gap-0.5 font-bold">
                    Read More <span className="material-symbols-outlined text-[10px]">open_in_new</span>
                  </a>
                </div>
                <p className="text-[11px] leading-snug opacity-95">Razorpay secure integration. Necessary for managing transaction flows and billing/subscription state.</p>
              </div>
            </div>
          )}

          {/* Toggle details */}
          <button
            onClick={() => setShowDetails(p => !p)}
            className={`text-xs font-black uppercase tracking-wider mb-4 py-2 px-4 rounded-xl flex items-center gap-2 transition-all border ${
              isDark 
                ? 'text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 border-indigo-500/20 hover:border-indigo-500/40' 
                : 'text-indigo-600 hover:text-indigo-700 bg-indigo-50/70 border-indigo-100/80 hover:bg-indigo-100/60 shadow-sm'
            }`}
          >
            <span className="material-symbols-outlined text-[16px]">{showDetails ? 'expand_less' : 'expand_more'}</span>
            <span>{showDetails ? 'Hide details' : 'View cookie details'}</span>
          </button>

          {/* Buttons */}
          <div className="flex flex-col gap-2">
            <button
              id="cookie-accept-all"
              onClick={handleAccept}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-black uppercase tracking-widest shadow-md shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all active:scale-95"
            >
              Accept All Cookies
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                id="cookie-essential-only"
                onClick={handleEssentialOnly}
                className={`flex-1 py-2 rounded-xl text-[11px] font-bold border transition-all active:scale-95 ${
                  isDark
                    ? 'border-white/10 text-slate-400 hover:border-white/20 hover:text-white bg-white/5'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 bg-slate-50'
                }`}
              >
                Essential Only
              </button>
              <button
                id="cookie-decline"
                onClick={handleDecline}
                className={`flex-1 py-2 rounded-xl text-[11px] font-bold border transition-all active:scale-95 ${
                  isDark
                    ? 'border-white/10 text-slate-400 hover:border-white/20 hover:text-white bg-white/5'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 bg-slate-50'
                }`}
              >
                Decline All
              </button>
            </div>
          </div>

          {/* Privacy Policy link */}
          <p className={`text-[10px] text-center mt-3 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            By clicking "Accept All" you agree to our{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">Privacy Policy</a>
            {', '}
            <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">Cookie Policy</a>
            {' and '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">Terms of Service</a>.
          </p>
        </div>
      </div>
    </>
  );
};

declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}

export default CookieConsentBanner;
